import posthog from 'posthog-js';
import { ReactNode, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useConfig } from '../config';
import { fromAccounts, fromAuth } from '../store';

type AnalyticsProperties = Record<string, unknown>;

let initializedKey: string | undefined;
let initializedHost: string | undefined;
let lastDistinctId: string | undefined;

const postHogIdentifyBlockedProperties = new Set([
  'email',
  'first_name',
  'last_name',
  'firstName',
  'lastName',
  'account_name',
]);

const getPostHogApiKey = (configApiKey?: string) => (
  configApiKey || process.env.REACT_APP_POSTHOG_API_KEY || ''
);

const getPostHogHost = (configHost?: string) => (
  configHost || process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com'
);

const removePostHogIdentifyPii = (properties?: AnalyticsProperties) => {
  if (!properties) {
    return properties;
  }

  return Object.fromEntries(
    Object.entries(properties).filter(([key]) => !postHogIdentifyBlockedProperties.has(key)),
  );
};

export const alias = (...args: any[]) => {
  const segmentAlias = window.analytics?.alias as ((...aliasArgs: any[]) => void) | undefined;
  segmentAlias?.(...args);
};

export const initPostHog = (apiKey?: string, host?: string) => {
  if (!apiKey || (initializedKey === apiKey && initializedHost === host)) {
    return;
  }

  posthog.init(apiKey, {
    api_host: host,
    capture_pageview: false,
    autocapture: false,
  });
  initializedKey = apiKey;
  initializedHost = host;
};

export const page = (...args: any[]) => {
  window.analytics?.page?.(...args);

  if (!initializedKey) {
    return;
  }

  const maybeProperties = args.find(arg => arg && typeof arg === 'object' && !Array.isArray(arg));
  posthog.capture('$pageview', {
    ...(maybeProperties ?? {}),
    $current_url: window.location.href,
    path: window.location.pathname,
    title: document.title,
  });
};

export const track = (eventName: string, properties?: AnalyticsProperties) => {
  window.analytics?.track?.(eventName, properties);

  if (!initializedKey) {
    return;
  }

  posthog.capture(eventName, properties);
};

export const identify = (distinctId?: string, properties?: AnalyticsProperties) => {
  if (!initializedKey || !distinctId) {
    return;
  }

  if (lastDistinctId && lastDistinctId !== distinctId) {
    posthog.reset();
  }

  posthog.identify(distinctId, removePostHogIdentifyPii(properties));
  lastDistinctId = distinctId;
};

export const identifyAccount = (accountId?: number, properties?: AnalyticsProperties) => {
  if (!initializedKey || !accountId) {
    return;
  }

  posthog.group('account', String(accountId), properties);
};

export const resetPostHogIdentity = () => {
  if (!initializedKey || !lastDistinctId) {
    return;
  }

  posthog.reset();
  lastDistinctId = undefined;
};

export const AnalyticsProvider = ({ children }: { children: ReactNode }) => {
  const { config } = useConfig();
  const user = useSelector(fromAuth.selectUser);
  const account = useSelector(fromAccounts.selectSelectedAccount);

  useEffect(() => {
    initPostHog(
      getPostHogApiKey(config?.posthogApiKey),
      getPostHogHost(config?.posthogHost),
    );
  }, [config?.posthogApiKey, config?.posthogHost]);

  useEffect(() => {
    if (!user) {
      resetPostHogIdentity();
      return;
    }

    identify(String(user.id), {
      user_id: user.id,
      system_role: user.roles,
      account_id: account?.id,
    });
  }, [user, account?.id, account?.name, config?.posthogApiKey, config?.posthogHost]);

  useEffect(() => {
    identifyAccount(account?.id, {
      name: account?.name,
      account_id: account?.id,
      account_uuid: account?.uuid,
    });
  }, [account?.id, account?.name, account?.uuid, config?.posthogApiKey, config?.posthogHost]);

  return <>{children}</>;
};
